import type {OperationDescriptor, RecordSourceSelectorProxy, Disposable, Variables} from 'relay-runtime'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {GraphQLTaggedNode} from 'react-relay'
import {createOperationDescriptor, getRequest} from 'relay-runtime'

/**
 * Stores the Relay data ID and query arguments needed to locate the
 * SearchResultItemConnection record that holds openIssueCount / closedIssueCount.
 */

interface SearchInfo {
  repoDataId: string
  queryArgs: {first: number; query: string; type: string; aggregations: boolean}
}

let searchInfo: SearchInfo | null = null
let retainDisposable: Disposable | null = null

/**
 * Save the search record location and retain the query operation so the
 * search record survives navigation away from the list page.
 */
export function retainOpenClosedCounts(opts: {
  environment: {retain: (operation: OperationDescriptor) => Disposable}
  queryNode: GraphQLTaggedNode
  variables: Variables
  repoDataId: string
  queryArgs: SearchInfo['queryArgs']
}): void {
  const {environment, queryNode, variables, repoDataId, queryArgs} = opts

  // Dispose previous retain if any (e.g., query args changed)
  retainDisposable?.dispose()

  const request = getRequest(queryNode)
  const operation = createOperationDescriptor(request, variables)

  retainDisposable = environment.retain(operation)

  searchInfo = {repoDataId, queryArgs}
}

/**
 * Release the retained operation and clear the search info.
 */
export function releaseOpenClosedCounts(): void {
  retainDisposable?.dispose()
  retainDisposable = null
  searchInfo = null
}

/**
 * Adjust open/closed counts in the Relay store.
 *
 * @param store - Relay store proxy from the updater callback
 * @param delta - +1 for reopen, -1 for close
 */
export function adjustOpenClosedCounts(store: RecordSourceSelectorProxy, delta: number): void {
  if (!searchInfo) return

  const repo = store.get(searchInfo.repoDataId)
  if (!repo) return

  const search = repo.getLinkedRecord('search', searchInfo.queryArgs)
  if (!search) return

  const openCount = (search.getValue('openIssueCount') as number) ?? 0
  const closedCount = (search.getValue('closedIssueCount') as number) ?? 0

  search.setValue(Math.max(0, openCount + delta), 'openIssueCount')
  search.setValue(Math.max(0, closedCount - delta), 'closedIssueCount')
}
